/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { ContentCard } from '@repo/shared-client/components/blocks'
import { Badge, Skeleton, Tooltip, TooltipContent, TooltipTrigger } from '@repo/shared-client/components/ui'
import { cn } from '@repo/shared-client/lib/utils'
import { UserRole } from '@repo/shared-common'
import { usePlaceCategoryOptions } from '@web/src/features/place/use-place-category-options'
import { useAuth } from '@web/src/hooks/use-auth'
import { useTranslation } from '@web/src/lib/intl/react'
import { LocateFixedIcon, NavigationIcon, StarIcon } from 'lucide-react'
import toast from 'react-hot-toast'
import LogoSymbol from 'src/components/primatives/logo-symbol/logo-symbol'
import SFMediaImage from 'src/components/primatives/sf-media-image/sf-media-image'
import { SFReviewHeader } from 'src/components/primatives/sf-review-status/sf-review-status'
import type { NSFWRatingEnum, PlaceCategoryEnum, ReviewEnum } from 'types/graphql'

export interface PlaceCardPlace {
  id: string
  slug?: string | null
  name?: string
  tagLine?: string
  reviewCount?: number
  reviewSum?: number
  reviewStatus?: ReviewEnum
  location?: string
  locationExact?: boolean
  distance?: number
  ownerId?: string | null
  supportersOnly?: boolean
  firstMedia?: {
    imageUrlMedium?: string
    reviewStatus?: ReviewEnum
    nsfwRating?: NSFWRatingEnum
  }
  category?: PlaceCategoryEnum
  tags?: {
    id: string
    name: string
  }[]
}

function computeDisplayRating(reviewCount?: number, reviewSum?: number): string | null {
  if (!reviewCount || reviewCount <= 0) return null
  return Math.min(Math.max(0, (reviewSum ?? 0) / reviewCount), 5.0).toFixed(1)
}

export const PlaceCardLoading = () => {
  return <Skeleton className="h-[200px] w-full rounded-md sm:h-[180px] sm:w-[48%] md:h-[180px] md:w-full" />
}

interface PlaceImageProps {
  place: PlaceCardPlace
  disableHoverAnimation?: boolean
}

const PlaceImage = (props: PlaceImageProps) => {
  const { disableHoverAnimation = false } = props

  return (
    <div
      className={cn(
        'relative h-full min-h-[inherit] w-full overflow-hidden bg-black',
        !disableHoverAnimation && 'transition-transform duration-[400ms] ease-out group-hover/content-card:scale-[1.06]'
      )}
      data-place-card-media=""
    >
      <SFMediaImage
        backgroundSize="contain"
        blurredBackdrop
        media={props.place.firstMedia}
        missingImage="/missing-place.webp"
      />
    </div>
  )
}

interface PlaceContentProps {
  place: PlaceCardPlace
  /** When true, rating is shown next to the title (no image overlay). */
  showRatingInline?: boolean
  /** Compact card row vs expanded stacked layout (from `ContentCard`). */
  collapsed?: boolean
}

const PlaceContent = (props: PlaceContentProps) => {
  const { t } = useTranslation()
  const { labelFor } = usePlaceCategoryOptions()
  const { isAuthenticated, isAtLeastRole } = useAuth()
  const { place } = props
  const locationName = place.locationExact ? place.location : t('Near {{location}}', { location: place.location ?? '' })
  const category = place.category
  const ratingText = computeDisplayRating(place.reviewCount, place.reviewSum)
  const showInlineRating = !props.collapsed && props.showRatingInline && ratingText
  const canSeeSupportersOnlyDetails = !place.supportersOnly || isAtLeastRole(UserRole.PREVIEW)
  const canSeeAddress = isAuthenticated && canSeeSupportersOnlyDetails
  const placeNameClassName = 'line-clamp-2 inline-block w-full break-words font-semibold text-xl tracking-wide'

  return (
    <div
      className="flex h-full w-full flex-col items-start gap-0 px-2 py-1"
      data-collapsed={props.collapsed ? 'true' : undefined}
    >
      <div className="flex w-full justify-between gap-2">
        {canSeeSupportersOnlyDetails ? (
          <Tooltip>
            <TooltipTrigger className="min-w-0 flex-1" render={<span className="inline-block w-full cursor-default" />}>
              <span className={placeNameClassName}>{place.name}</span>
            </TooltipTrigger>
            <TooltipContent side="top">{place.name}</TooltipContent>
          </Tooltip>
        ) : (
          <span className="inline-block min-w-0 flex-1 cursor-default">
            <span aria-hidden className={cn(placeNameClassName, 'pointer-events-none select-none blur-sm')}>
              {place.name}
            </span>
          </span>
        )}
        {showInlineRating ? (
          <div className="flex shrink-0 items-center gap-1 rounded-md bg-black/70 px-2 py-0.5">
            <StarIcon className="size-3 fill-red-600 text-red-600" />
            <span className="font-semibold text-sm text-white">{ratingText}</span>
          </div>
        ) : null}
      </div>
      <div className="flex max-h-[60px] w-full flex-wrap items-start justify-start gap-1 overflow-hidden py-1">
        {category ? (
          <Badge key="place-category" variant="secondary">
            {labelFor(category)}
          </Badge>
        ) : null}
        {place.tags?.map((tag) => {
          return (
            <Badge key={tag.id} variant="outline">
              {tag.name}
            </Badge>
          )
        })}
      </div>

      <p className="line-clamp-2 pb-1 text-muted-foreground">{place.tagLine}</p>

      <div className="grow" />

      <div
        className={cn(
          'grid w-full grid-cols-1 gap-1 text-muted-foreground text-sm md:grid-cols-[auto_1fr] md:gap-2',
          props.collapsed ? 'pt-0' : 'pt-1'
        )}
      >
        {canSeeAddress ? (
          <>
            <div className="flex items-center gap-1 text-[0.85rem]">
              <LocateFixedIcon className="size-4 shrink-0" />
              <Tooltip>
                <TooltipTrigger className="min-w-0" render={<span className="inline-flex min-w-0 cursor-default" />}>
                  <span className="line-clamp-1">{locationName}</span>
                </TooltipTrigger>
                <TooltipContent side="top">{locationName}</TooltipContent>
              </Tooltip>
            </div>
            <div className={cn('flex items-center gap-1 text-[0.85rem]', props.collapsed && 'hidden')}>
              <NavigationIcon className="size-4 shrink-0" />
              <span className="whitespace-nowrap">
                {place.distance != null ? t('{{distance}} km', { distance: place.distance.toFixed(0) }) : null}
              </span>
            </div>
          </>
        ) : (
          <div className="flex items-center gap-1 whitespace-nowrap text-[0.85rem]">
            <LocateFixedIcon className="size-4 shrink-0" />
            <span className="line-clamp-1">{place.supportersOnly ? t('Supporters Only') : t('Members Only')}</span>
          </div>
        )}
      </div>
    </div>
  )
}

function RatingOverlay({ reviewCount, reviewSum }: { reviewCount?: number; reviewSum?: number }) {
  const value = computeDisplayRating(reviewCount, reviewSum)
  if (!value) return null

  return (
    <Badge className="absolute top-4 left-4 z-1 bg-black/70" size="default">
      <StarIcon className="text-primary" fill="currentColor" />
      {value}
    </Badge>
  )
}

function SupportersOnlyBadge({ className }: { className?: string }) {
  const { t } = useTranslation()

  return (
    <Badge className={cn('max-w-[calc(100%-1rem)] bg-black/70 py-3', className)} size="default">
      <span className="relative block size-3.5 shrink-0 overflow-hidden">
        <LogoSymbol imageClassName="size-3.5 object-contain drop-shadow-[1px_1px_1px_#000000]" />
      </span>
      {t('Supporters Only')}
    </Badge>
  )
}

function SupportersOnlyOverlay() {
  return <SupportersOnlyBadge className="absolute bottom-4 left-4 z-1" />
}

export type PlaceCardLayoutMode = 'compact' | 'regular'

interface PlaceCardProps {
  cardLayout: PlaceCardLayoutMode
  place: PlaceCardPlace
  /** Tailwind rounding for the card root (e.g. `rounded-none`). Defaults to `rounded-2xl`. */
  cardBorderRadiusClassName?: string
  /** When true, skips the image scale-on-hover effect on the media area. */
  disableHoverAnimation?: boolean
}

const PlaceCard = (props: PlaceCardProps) => {
  const { cardBorderRadiusClassName = 'rounded-2xl', disableHoverAnimation = false, place } = props
  const { t } = useTranslation()
  const { currentUser, hasPermission, isAtLeastRole } = useAuth()
  const hasMedia = Boolean(place.firstMedia)
  const isCompact = props.cardLayout === 'compact'
  const isSupporterOrPreview = isAtLeastRole(UserRole.PREVIEW)
  const canOpenListing =
    !place.supportersOnly ||
    isSupporterOrPreview ||
    place.ownerId === currentUser?.profile?.id ||
    hasPermission('place:review')

  const renderBody = (collapsed: boolean) => (
    <PlaceContent collapsed={collapsed} place={place} showRatingInline={!hasMedia} />
  )

  const imageNode = <PlaceImage disableHoverAnimation={disableHoverAnimation} place={place} />

  const imageControls = (
    <>
      <RatingOverlay reviewCount={place.reviewCount} reviewSum={place.reviewSum} />
      {place.supportersOnly ? <SupportersOnlyOverlay /> : null}
    </>
  )

  return (
    <ContentCard
      bodyClassName="p-1 sm:p-2"
      bodyCollapsed={renderBody(true)}
      bodyExpanded={renderBody(false)}
      className={cardBorderRadiusClassName}
      compactImageMinHeight={160}
      compactImageWidth={200}
      expandedImageHeight={180}
      header={<SFReviewHeader reviewStatus={place.reviewStatus} />}
      image={imageNode}
      imageControls={imageControls}
      imageSide="left"
      layout={isCompact ? 'compact' : 'expanded'}
      onClick={
        canOpenListing
          ? undefined
          : () => {
              toast.error(t('You must be a supporter to see this listing'))
            }
      }
      params={canOpenListing ? ({ placeIdOrSlug: place.slug ?? place.id } as never) : undefined}
      to={canOpenListing ? '/place/$placeIdOrSlug' : undefined}
    />
  )
}

export default PlaceCard
